import {
  ActionCreatorWithPayload,
  PayloadAction,
  UnknownAction,
  createSlice,
} from '@reduxjs/toolkit'
import { GenericOperationState } from '@repo/core'
import { EXTERNAL_PAGE } from '@repo/shared-types'
import { ExternalPageItem } from '@repo/tools-types'
import { Reducer } from 'redux'

export interface ExternalPageSliceEntry {
  items: ExternalPageItem[] | null
  isLoading?: boolean
  hasError?: boolean
}

type ExternalPageSliceState = Partial<
  Record<EXTERNAL_PAGE, GenericOperationState<ExternalPageSliceEntry>>
>
const externalPageSlice = createSlice({
  name: '@tools/externalTool/page',
  initialState: {} as ExternalPageSliceState,
  reducers: {
    getRequest: (state, action: PayloadAction<{ pageId: EXTERNAL_PAGE }>) => {
      const { pageId } = action.payload
      state[pageId] = {
        payload: { items: null },
        error: false,
        meta: {
          fetchState: 'loading',
        },
      }
    },
    getResponse: (
      state,
      action: PayloadAction<
        { pageId: EXTERNAL_PAGE } & GenericOperationState<ExternalPageSliceEntry>
      >,
    ) => {
      const { pageId, ...rest } = action.payload
      state[pageId] = { ...rest, meta: { ...rest.meta, fetchState: 'done' } }
    },
    refreshRequest: (state, action: PayloadAction<{ pageId: EXTERNAL_PAGE }>) => {
      const { pageId } = action.payload
      state[pageId] = {
        payload: { items: null },
        error: false,
        meta: {
          fetchState: 'refreshing',
        },
      }
    },
    refreshResponse: (
      state,
      action: PayloadAction<
        { pageId: EXTERNAL_PAGE } & GenericOperationState<ExternalPageSliceEntry>
      >,
    ) => {
      const { pageId, ...rest } = action.payload
      state[pageId] = { ...rest, meta: { ...rest.meta, fetchState: 'done' } }
    },
    clearData: (state, action: PayloadAction<{ pageId: EXTERNAL_PAGE }>) => {
      delete state[action.payload.pageId]
    },
  },
})

export const {
  actions: externalPageDataActions,
  reducer: externalPageDataReducers,
}: {
  actions: {
    getRequest: ActionCreatorWithPayload<{ pageId: EXTERNAL_PAGE }>
    getResponse: ActionCreatorWithPayload<
      { pageId: EXTERNAL_PAGE } & GenericOperationState<ExternalPageSliceEntry>
    >
    refreshRequest: ActionCreatorWithPayload<{ pageId: EXTERNAL_PAGE }>
    refreshResponse: ActionCreatorWithPayload<
      { pageId: EXTERNAL_PAGE } & GenericOperationState<ExternalPageSliceEntry>
    >
    clearData: ActionCreatorWithPayload<{ pageId: EXTERNAL_PAGE }>
  }
  reducer: Reducer<ExternalPageSliceState, UnknownAction>
} = externalPageSlice
